import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import CreateSelectEntity from "@/components/CreateSelectEntity";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import Rating from "@/components/Rating";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {ClubAPI} from "@/api/club";
import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";

import {
	DEFAULT_CATEGORY_ID,
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
	dateFormat,
	baseExternTrainingParameters,
	externalValuationOptions,
	playerRoles,
	proExternTrainingParameters,
	valuationsTypes,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getTextAreaField,
	getTextField,
} from "@/utils/functions";
import {useCategories, usePlayers} from "@/utils/hooks";

import "../Tryout/index.scss";
import {useTranslation} from "react-i18next";

const HandleTryoutPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
		onlyPro: false,
	});
	const {players} = usePlayers({
		setLoading,
		handleNotification: undefined,
		prospect: true,
	});

	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});
	const [saveOption, setSaveOption] = useState(edit ? "select" : "new");
	const [valuationParameters, setValuationParameters] = useState([]);
	const [clubs, setClubs] = useState([]);

	const getClubs = async () => {
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const createPlayer = async () => {
		try {
			const body = {
				firstName: fields?.firstName ? fields.firstName : "",
				lastName: fields?.lastName,
				dateOfBirth: fields?.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				favoriteFoot: fields?.favoriteFoot,
				goalkeeper: true,
				primaryRole: "Portiere",
				prospect: true,
				playerCategoryId: DEFAULT_CATEGORY_ID,
			};

			const response = await PlayerAPI.create(body);
			return response;
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const createClub = async (name) => {
		try {
			const response = await ClubAPI.create({name, prospect: true});
			setFields({
				...fields,
				prospectClub: {value: response.id, label: response.name},
			});
			getClubs();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getValuation = async () => {
		setLoading(true);
		try {
			const response = await PlayerValuationAPI.getById(id);

			response.prospectClub = {
				value: response.club?.id,
				label: response.club?.name,
			};

			response.prospectCategory = {
				value: response.category?.id,
				label: response.category?.name,
			};

			response.prospectPlayer = {
				value: response.player?.id,
				label:
					(response.player?.firstName || "") + " " + response.player?.lastName,
			};

			response.rating =
				response.finalOverallRating !== null &&
				response.finalOverallRating !== undefined
					? externalValuationOptions.filter(
							(v) => v.value === response.finalOverallRating.toString(),
						)
					: undefined;

			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getClubs();
	}, []);

	useEffect(() => {
		if (edit) {
			if (categories?.length && clubs?.length && players?.length) {
				getValuation();
			}
		}
	}, [edit, categories, clubs, players]);

	const getRequiredFields = () => {
		const reqFields = ["valuationDate", "valuationAuthor", "valuationText"];

		if (saveOption === "new") {
			reqFields.push("prospectClub", "prospectCategory", "lastName");
		} else {
			reqFields.push("prospectClub", "prospectCategory", "prospectPlayer");
		}

		return reqFields;
	};

	const saveValuation = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			const body = Object.assign({}, fields);

			if (saveOption === "new") {
				const playerResponse = await createPlayer();
				body.playerId = playerResponse?.id;
			} else {
				body.playerId = body.prospectPlayer?.value;
			}

			body.clubId = body.prospectClub?.value;
			body.club = {
				id: body.prospectClub?.value,
				name: body.prospectClub?.label,
			};
			body.playerCategoryId = body.prospectCategory?.value;
			body.finalOverallRating = body.rating?.value;

			const isCategoryPro =
				getCategoryNumber(fields?.prospectCategory?.label) > 13;
			body.valuationType = isCategoryPro ? 3 : 5;

			body.valuationDate = moment(body.valuationDate).format("YYYY-MM-DD");

			try {
				const valuationData = edit
					? await PlayerValuationAPI.update(id, body)
					: await PlayerValuationAPI.create(body);
				setTimeout(() => {
					const currentId = id || valuationData?.id;
					navigate("/scouting/tryouts/" + currentId);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveValuation();
	};

	const goBack = () => {
		navigate(-1);
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{
			label: "Provini",
			path: "/scouting/tryouts",
		},
	];

	const playerLabel = fields?.player?.firstName
		? fields?.player?.firstName + " " + fields?.player?.lastName
		: fields?.player?.lastName;

	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label:
					moment(fields?.valuationDate).format(dateFormat) + " " + playerLabel,
				path: "/scouting/tryouts/" + id,
			},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const getFinalAvg = () => {
		let totalRatings = 0,
			totalRatingsInserted = 0;

		valuationParameters.forEach((i) => {
			const currentValue = fields?.[i.fieldKeyRating];
			if (currentValue && currentValue > 0) {
				totalRatings += currentValue;
				totalRatingsInserted++;
			}
		});

		return !totalRatingsInserted
			? 0
			: +parseFloat(totalRatings / totalRatingsInserted).toFixed(2);
	};

	const getCategoryNumber = (category) => {
		return parseInt(category?.split(" ")?.[1]);
	};

	const getParameters = () => {
		setValuationParameters(
			fields?.valuationType?.value === "quick"
				? []
				: !fields?.prospectCategory?.label ||
					  getCategoryNumber(fields?.prospectCategory?.label) > 13
					? proExternTrainingParameters(t)
					: baseExternTrainingParameters(t),
		);
	};

	useEffect(() => {
		getParameters();
	}, [fields?.valuationType, fields?.prospectCategory]);

	const selectPlayers = players.map((p) => {
		return {
			value: p.id,
			label: p.firstName + " " + p.lastName,
		};
	});
	const currentPlayer = players?.filter(
		(p) => p.id === fields?.prospectPlayer?.value,
	)?.[0];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-12 padding-small">
						<VLFCard
							title="Informazioni giocatore"
							content={
								<div className="row no-margin single-area-container">
									<div className="col-12 padding-small">
										<CreateSelectEntity
											currentOption={saveOption}
											setCurrentOption={setSaveOption}
										/>
									</div>
									{saveOption === "new" ? (
										<>
											<div className="col-12 col-sm-6 col-xl-3">
												{getTextField({
													...defaultTextField,
													fieldKey: "firstName",
													label: t("name"),
												})}
											</div>
											<div className="col-12 col-sm-6 col-xl-3">
												{getTextField({
													...defaultTextField,
													fieldKey: "lastName",
													label: "Cognome",
													required: true,
												})}
											</div>
											<div className="col-12 col-sm-6 col-xl-3">
												<VLFDatePicker
													label="Data di nascita"
													value={fields.dateOfBirth}
													onChange={(value) => {
														setFields({...fields, dateOfBirth: value});
													}}
													format="DD-MM-YYYY"
												/>
											</div>
											<div className="col-12 col-sm-6 col-xl-3">
												<VLFSelectNew
													options={playerRoles}
													value={fields.playerPrimaryRole}
													onChange={(newValue) => {
														setFields({
															...fields,
															playerPrimaryRole: newValue,
														});
													}}
													sorted={false}
													label="Ruolo"
													required
												/>
											</div>
											<div className="col-12 col-sm-6 col-xl-3">
												{getTextField({
													...defaultTextField,
													fieldKey: "favoriteFoot",
													label: "Piede principale",
												})}
											</div>
										</>
									) : (
										<>
											<div className="col-12 col-sm-6">
												<VLFSelectNew
													options={selectPlayers}
													value={fields?.prospectPlayer}
													onChange={(value) => {
														setErrors([]);
														setFields({...fields, prospectPlayer: value});
													}}
													required
													helperText={
														errors.indexOf("prospectPlayer") !== -1
															? REQUIRED_FIELD
															: ""
													}
													error={errors.indexOf("prospectPlayer") !== -1}
													label={"Giocatore"}
												/>
											</div>
											{fields?.prospectPlayer && (
												<div className="col-12 col-sm-6 col-xl-3">
													<VLFDatePicker
														label="Data di nascita"
														value={
															currentPlayer && currentPlayer?.dateOfBirth
																? moment(currentPlayer?.dateOfBirth).format(
																		dateFormat,
																	)
																: "---"
														}
														editable={false}
													/>
												</div>
											)}
											{fields?.prospectPlayer && (
												<div className="col-12 col-sm-6 col-xl-3">
													<VLFTextField
														value={
															currentPlayer?.goalkeeper
																? "Portiere"
																: currentPlayer?.primaryRole
														}
														label="Ruolo"
														editable={false}
													/>
												</div>
											)}
										</>
									)}
									<div className="col-12 col-sm-6 col-xl-3">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "height",
											label: "Altezza (cm)",
										})}
									</div>
									<div className="col-12 col-sm-6 col-xl-3">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "weight",
											label: "Peso (kg)",
										})}
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni provino"
							content={
								<>
									<div className="row no-margin">
										<div className="col-12 col-lg-10">
											<div className="row">
												<div className="col-12 col-md-6 col-xl-4">
													<VLFSelectNew
														options={valuationsTypes}
														value={fields.valuationType}
														onChange={(newValue) => {
															setErrors([]);
															setFields({
																...fields,
																valuationType: newValue,
															});
														}}
														label="Tipologia"
														required
														helperText={
															errors.indexOf("valuationType") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("valuationType") !== -1}
													/>
												</div>
												<div className="col-12 col-md-6 col-xl-4">
													<VLFDatePicker
														label="Data"
														value={fields.valuationDate}
														onChange={(value) => {
															setErrors([]);
															setFields({...fields, valuationDate: value});
														}}
														required
														format="DD-MM-YYYY"
														size="small"
														helperText={
															errors.indexOf("valuationDate") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("valuationDate") !== -1}
													/>
												</div>
												<div className="col-12 col-md-6 col-xl-4">
													{getTextField({
														...defaultTextField,
														fieldKey: "valuationAuthor",
														label: "Relatore",
														required: true,
													})}
												</div>
												<div className="col-12 col-md-6 col-xl-4">
													<VLFSelectNew
														options={clubs.map((c) => {
															return {
																value: c.id,
																label: c.name,
															};
														})}
														value={fields.prospectClub}
														onChange={(newValue) => {
															setErrors([]);
															setFields({
																...fields,
																prospectClub: newValue,
															});
														}}
														onCreateOption={createClub}
														label="Squadra"
														required
														helperText={
															errors.indexOf("prospectClub") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("prospectClub") !== -1}
													/>
												</div>
												<div className="col-12 col-md-6 col-xl-4">
													<VLFSelectNew
														options={categories}
														sorted={false}
														value={fields.prospectCategory}
														onChange={(newValue) => {
															setErrors([]);
															setFields({
																...fields,
																prospectCategory: newValue,
															});
														}}
														label="Categoria"
														required
														helperText={
															errors.indexOf("prospectCategory") !== -1
																? REQUIRED_FIELD
																: ""
														}
														error={errors.indexOf("prospectCategory") !== -1}
													/>
												</div>
												<div className="col-sm-12 col-md-6 col-xl-4">
													{getTextField({
														...defaultTextField,
														fieldKey: "trialPeriod",
														label: "Periodo di prova",
													})}
												</div>
											</div>
										</div>
										<div className="col-12 col-lg-2">
											<div className="d-flex justify-content-center h-100 flex-column bordered-left">
												<div className="field-label-container justify-content-center">
													<div className="field-label">Voto finale</div>
												</div>
												<div className="stat-circle-container">
													<div className="stat-container">
														<div className="value">{getFinalAvg()}</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Dettagli"
							content={
								<>
									<div className="row no-margin">
										{valuationParameters.map((el, k) => {
											return (
												<div key={k} className="col-sm-12 col-md-6">
													<div className="field-label-container">
														<div className="field-label">{el.label}</div>
													</div>
													<Rating
														selectedRating={fields[el.fieldKeyRating]}
														setSelectedRating={(value) => {
															setFields({
																...fields,
																[el.fieldKeyRating]: value,
															});
														}}
													/>
													{getTextAreaField({
														...defaultTextField,
														fieldKey: el.fieldKey,
														minRows: 3,
														maxRows: 3,
													})}
												</div>
											);
										})}
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={externalValuationOptions}
												value={fields.rating}
												sorted={false}
												onChange={(newValue) => {
													setFields({
														...fields,
														rating: newValue,
													});
												}}
												label="Esito"
											/>
										</div>
										{fields?.valuationType?.value === "quick" ? (
											<div className="col-sm-12 col-md-6">
												<div className="field-label-container">
													<div className="field-label">Punteggio</div>
												</div>
												<Rating
													selectedRating={fields.valuationRating}
													setSelectedRating={(value) => {
														setFields({
															...fields,
															valuationRating: value,
														});
													}}
												/>
											</div>
										) : null}
									</div>
									<div className="row no-margin">
										<div className="col-12">
											{getTextAreaField({
												...defaultTextField,
												fieldKey: "finalOverallValuation",
												label: "Valutazione complessiva finale",
											})}
										</div>
									</div>
									<div className="row no-margin">
										<div className="col-12">
											{getTextAreaField({
												...defaultTextField,
												fieldKey: "valuationText",
												label: "Relazione generale",
												required: true,
												minRows: 10,
												maxRows: 10,
											})}
										</div>
									</div>
								</>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleTryoutPage;
